/**
 * Collapse state for the routing-rule sections of a queue list.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Sections default OPEN and one button drives them all, while each section's
 * own chevron still works — both write the same set, so the two controls can
 * never disagree. Mirrors the Live tab's path-level collapse deliberately: the
 * same gesture should behave the same wherever a list is grouped.
 */

import { useCallback, useMemo, useState } from 'react';

export interface RuleCollapse {
  isOpen: (key: string) => boolean;
  toggleOne: (key: string) => void;
  toggleAll: () => void;
  allCollapsed: boolean;
  /** False when there is nothing worth collapsing (0 or 1 section). */
  worthShowing: boolean;
}

export function useRuleCollapse(keys: string[]): RuleCollapse {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const allCollapsed = useMemo(
    () => keys.length > 0 && keys.every((k) => collapsed.has(k)),
    [keys, collapsed],
  );

  const toggleAll = useCallback(
    () => setCollapsed(allCollapsed ? new Set() : new Set(keys)),
    [allCollapsed, keys],
  );

  const toggleOne = useCallback(
    (key: string) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  return {
    isOpen: (key) => !collapsed.has(key),
    toggleOne,
    toggleAll,
    allCollapsed,
    // One section is the whole list — a collapse button there just hides
    // everything behind an extra click.
    worthShowing: keys.length > 1,
  };
}
